import { Button, TextField, LinearProgress, Tooltip, Chip } from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import Proptypes from "prop-types"
import { TYPES } from "../../../../../config/constants/typesReducers";
import { SignatureContext } from "../../../context/SignatureContext";
import { useContext, useState, useMemo, useRef } from "react";
import { ModalSign } from "../../viewer-field/components/ModalSign";
import { FddApi } from "../../../../../ui/services/server-fdd";
import { useNavigate, useLocation } from "react-router-dom";
import { getIdFromPath } from "../../../../../ui/utils/getIdFromPath";
import { useNotification } from "../../../../../ui/context/notification/NotificationContext";
import { formatISODate, DEFAULT_DATE_FORMAT } from "../../../../../ui/utils/dateFormat";

export const FormDesktop = ({
    currentField,
    sortedFields,
    indexSortedField,
    handleBeforeField,
    handleNextField,
    valueField, 
    setValueField
}) => {
    const { signatureDispatch, signatureState } = useContext(SignatureContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showNotification } = useNotification();

    const sortedFieldsArray = useMemo(() => Object.values(sortedFields || {}), [sortedFields]);
    const totalFields = sortedFieldsArray.length;
    const filledCount = useMemo(() =>
        sortedFieldsArray.filter(f => !f.isEmpty).length,
    [sortedFieldsArray]);
    const allFieldsFilled = filledCount === totalFields;
    const progress = totalFields > 0 ? (filledCount / totalFields) * 100 : 0;
    const isLastField = indexSortedField === totalFields - 1;

    const handleFinish = async () => {
        setIsSubmitting(true);
        try {
            const signatureId = getIdFromPath(location.pathname);
            
            if (valueField) {
                signatureDispatch({
                    type: TYPES.SIGNATURE.EDIT_FIELDS,
                    payload: { field: currentField.field, value: valueField }
                });
            }

            const response = await FddApi.completeSignature(signatureId, signatureState.signature.fieldsByPage);
            
            if (response.hasError) throw new Error(response.message);

            showNotification('¡Firma completada exitosamente!', 'success', 5000);
            navigate('/');
        } catch (error) {
            console.error('[FINISH SIGNATURE] Error:', error);
            showNotification('Error al completar la firma: ' + error.message, 'error', 6000);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='form-desktop signature__form'>
            {/* Barra de progreso — siempre visible arriba */}
            <div style={{ marginBottom: '10px', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span className='sm-text grey-text'>
                        Campos: <strong>{filledCount} de {totalFields}</strong>
                    </span>
                    {allFieldsFilled && (
                        <Chip
                            icon={<CheckCircleOutline style={{ fontSize: 16 }} />}
                            label="Todo listo"
                            color="success"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </div>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={allFieldsFilled ? 'success' : 'primary'}
                    sx={{ borderRadius: 4, height: 6 }}
                />
            </div>

            <form action="">
                {/* Zona scrollable: título + campo actual */}
                <div className="signature__form--form">
                    <div className="form-desktop__title" style={{ marginBottom: '12px' }}>
                        <p className='xl-text'>{currentField.label}</p>
                        <p className='sm-text grey-text'>
                            {currentField.field?.type === 'sign'
                                ? 'Dibuja o sube tu firma — se aplicará en todos los campos'
                                : 'Completa este campo'}
                        </p>
                    </div>
                    <TypeField
                        field={currentField.field}
                        value={valueField}
                        setValue={setValueField}
                    />
                </div>

                {/* Zona fija inferior: navegación + Finalizar */}
                <div className="signature__form--footer">
                    <div className="form-desktop__buttons">
                        <Button
                            onClick={handleBeforeField}
                            variant="text"
                            disableElevation
                            style={{ visibility: indexSortedField > 0 ? 'visible' : 'hidden' }}
                            color='secondary'>
                            Anterior
                        </Button>
                        <p className='sm-text grey-text'>{indexSortedField + 1} / {totalFields}</p>
                        <Button
                            onClick={handleNextField}
                            variant="text"
                            disabled={currentField.isEmpty || isLastField}
                            disableElevation
                            color='primary'>
                            Siguiente
                        </Button>
                    </div>

                    <Tooltip
                        title={!allFieldsFilled ? `Faltan ${totalFields - filledCount} campo(s) por completar` : ''}
                        arrow>
                        <span style={{ display: 'block' }}>
                            <Button
                                onClick={handleFinish}
                                variant="contained"
                                disabled={!allFieldsFilled || isSubmitting}
                                disableElevation
                                color='primary'
                                fullWidth
                                size="large"
                                sx={{ borderRadius: 2, py: 1.5, fontSize: '1rem', fontWeight: 600 }}>
                                {isSubmitting ? 'Enviando...' : '✓ Finalizar Firma'}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </form>
        </div>
    )
}

export const TypeField = ({field, value, setValue}) => {
    const { signatureState: { signature:{ fieldsByPage } }, signatureDispatch } = useContext(SignatureContext);
    const canvasRef = useRef(null);
  
    const handleBlurField = (event) => {
        const { value } = event.target;
    
        const action = {
          type: TYPES.SIGNATURE.EDIT_FIELDS,
          payload: {
            field: field,
            value: value,
          }
        }
        signatureDispatch(action);
    }
    
  
    const getTypeField = (fieldData) => {
      switch (fieldData.type) {
        case 'text':
          return (
            <TextField
              name={fieldData.name}
              label={fieldData.label}
              onBlur={handleBlurField}
              defaultValue={fieldsByPage[fieldData.page]?.[fieldData.id]?.value || ''}
              variant="outlined"
              fullWidth
              required/>
          )
        case 'sign':
          return (
            <ModalSign
              elementRef={canvasRef}
              value={value}
              setValue={(newValue) => {
                setValue(newValue);
                if (newValue) {
                  signatureDispatch({
                    type: TYPES.SIGNATURE.EDIT_FIELDS,
                    payload: { field: fieldData, value: newValue }
                  });
                }
              }}
              onClear={() => {
                setValue('');
                signatureDispatch({
                  type: TYPES.SIGNATURE.EDIT_FIELDS,
                  payload: { field: fieldData, value: '' }
                });
              }}
              sign={fieldsByPage[fieldData.page]?.[fieldData.id]?.value}
            />
          )
        case 'date':
          return (
            <TextField
              name={fieldData.name}
              label={fieldData.label ?? `Fecha (${fieldData.dateFormat || DEFAULT_DATE_FORMAT})`}
              type="date"
              onBlur={(event) => {
                const isoValue = event.target.value;
                if (!isoValue) return;
                const formatted = formatISODate(isoValue, fieldData.dateFormat);
                signatureDispatch({
                  type: TYPES.SIGNATURE.EDIT_FIELDS,
                  payload: { field: fieldData, value: formatted }
                });
              }}
              defaultValue={fieldsByPage[fieldData.page]?.[fieldData.id]?.value || ''}
              variant="outlined"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              helperText={`Formato: ${fieldData.dateFormat || DEFAULT_DATE_FORMAT}`}
            />
          )
        default:
          return (
            <TextField
              name={fieldData.name}
              label={fieldData.label}
              onBlur={handleBlurField}
              defaultValue={fieldsByPage[fieldData.page]?.[fieldData.id]?.value || ''}
              variant="outlined"
              fullWidth
              required/>
          )
      }
    }
  
    return (
      <>
        {getTypeField(field)}
      </>
    )
  }

FormDesktop.propTypes = {
    currentField: Proptypes.object,
    sortedFields: Proptypes.object,
    indexSortedField: Proptypes.number,
    handleBeforeField: Proptypes.func,
    handleNextField: Proptypes.func,
    valueField: Proptypes.string,
    setValueField: Proptypes.func,
}

TypeField.propTypes = {
    field: Proptypes.object,
    value: Proptypes.string,
    setValue: Proptypes.func,
}